import { useCallback, useState } from "react";

/**
 * Remembers a user's last creative-generation selections in localStorage, keyed per
 * creative type (e.g. "makeover"), so the studio re-opens with the same engine /
 * quality / intensity next time. Generic on purpose — any creative generator can reuse
 * it for its own selections.
 *
 *   const [prefs, updatePrefs] = useCreativePreferences("makeover", { engine, quality, intensity });
 *   updatePrefs({ engine: "value" });
 */
const STORAGE_PREFIX = "creativePrefs:";

export default function useCreativePreferences<T extends object>(
  creativeType: string,
  defaults: T
): [T, (patch: Partial<T>) => void] {
  const key = STORAGE_PREFIX + creativeType;

  const [prefs, setPrefs] = useState<T>(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
      // Spread defaults first so newly-added fields always have a value, and stored
      // values that are no longer valid get overwritten on the next change.
      return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
    } catch {
      return defaults;
    }
  });

  const update = useCallback(
    (patch: Partial<T>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* ignore quota / private-mode write errors — persistence is best-effort */
        }
        return next;
      });
    },
    [key]
  );

  return [prefs, update];
}
